import { Box, MenuItem, Select, TextField } from '@mui/material';
import { Cell, CellMetadata, CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import React, { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material';
import { useGridTheme } from '@/shared/ui/data/EditableGrid/theme';

interface IProps {
  cell?: Cell;
  isHeader?: boolean;
  focused?: boolean;
  selected?: boolean;
  editing?: boolean;
  disabled?: boolean;
  rowSx?: SxProps<Theme>;
  onClick?: React.MouseEventHandler;
  onMouseDown?: React.MouseEventHandler;
  onMouseEnter?: React.MouseEventHandler;
  onDoubleClick?: React.MouseEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
  onValueChange?: (newValue: string) => void;
  onValueCommit?: () => void;
  tempValue?: CellValue;
  children?: ReactNode;
}

export function GridCell({
  cell,
  isHeader = false,
  focused = false,
  selected = false,
  editing = false,
  disabled = false,
  rowSx,
  onClick,
  onMouseDown,
  onMouseEnter,
  onDoubleClick,
  onKeyDown,
  onValueChange,
  onValueCommit,
  tempValue,
  children,
}: IProps) {
  const cellRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const { getCellSx } = useGridTheme();

  useEffect(() => {
    if (editing) {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      } else if (selectRef.current) {
        selectRef.current.focus();
      }
    } else if (focused && cellRef.current) {
      cellRef.current.focus();
    }
  }, [editing, focused]);

  const cellSx = getCellSx({
    isHeader,
    focused,
    selected,
    editing,
    disabled,
    diff: cell?.diff,
  });
  const mergedSx = (rowSx ? [cellSx, rowSx] : cellSx) as SxProps<Theme>;

  if (isHeader) return <Box sx={mergedSx}>{children}</Box>;

  if (editing) {
    const metadata = cell?.metadata;

    if (metadata?.type === 'dropdown') {
      return (
        <DropdownCell
          metadata={metadata}
          sx={mergedSx}
          selectRef={selectRef}
          tempValue={tempValue}
          onValueChange={onValueChange}
          onValueCommit={onValueCommit}
          onKeyDown={onKeyDown}
        />
      );
    } else if (metadata?.type === 'number') {
      return (
        <NumberCell
          metadata={metadata}
          sx={mergedSx}
          inputRef={inputRef}
          tempValue={tempValue}
          onValueChange={onValueChange}
          onKeyDown={onKeyDown}
        />
      );
    } else if (metadata?.type === 'text') {
      return (
        <TextCell
          metadata={metadata}
          sx={mergedSx}
          inputRef={inputRef}
          tempValue={tempValue}
          onValueChange={onValueChange}
          onKeyDown={onKeyDown}
        />
      );
    } else throw new Error(`Unknown cell type: '${metadata?.type}'`);
  } else {
    return (
      <ReadonlyCell
        ref={cellRef}
        sx={mergedSx}
        cell={cell}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onDoubleClick={onDoubleClick}
        onKeyDown={onKeyDown}
        disabled={disabled}
      />
    );
  }
}

function DropdownCell({
  metadata,
  sx,
  selectRef,
  tempValue,
  onValueChange,
  onValueCommit,
  onKeyDown,
}: {
  metadata: CellMetadata & { type: 'dropdown' };
  sx: SxProps<Theme>;
  selectRef: RefObject<HTMLSelectElement | null>;
  tempValue: IProps['tempValue'];
  onValueChange: IProps['onValueChange'];
  onValueCommit: IProps['onValueCommit'];
  onKeyDown: IProps['onKeyDown'];
}) {
  const options = metadata.options;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(true);
  }, []);

  const handleChange = (value: string) => {
    onValueChange?.(value);
    setTimeout(() => {
      onValueCommit?.();
    }, 0);
  };

  return (
    <Box sx={{ ...sx, p: 0.5 }}>
      <Select
        inputRef={selectRef}
        value={tempValue}
        onChange={(e) => handleChange(`${e.target.value}`)}
        onKeyDown={onKeyDown as any}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        size="small"
        fullWidth
        autoFocus
        sx={{
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        }}
      >
        <MenuItem value="">값을 선택해주세요.</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function NumberCell({
  metadata,
  sx,
  inputRef,
  tempValue,
  onValueChange,
  onKeyDown,
}: {
  metadata: CellMetadata & { type: 'number' };
  sx: SxProps<Theme>;
  inputRef: RefObject<HTMLInputElement | null>;
  tempValue: IProps['tempValue'];
  onValueChange: IProps['onValueChange'];
  onKeyDown: IProps['onKeyDown'];
}) {
  return (
    <Box sx={{ ...sx, p: 0.5 }}>
      <TextField
        inputRef={inputRef}
        type="number"
        value={tempValue?.toString() ?? ''}
        onChange={(e) => onValueChange?.(e.target.value)}
        onKeyDown={onKeyDown as any}
        size="small"
        fullWidth
        autoFocus
        inputProps={{
          min: metadata.min,
          max: metadata.max,
          step: metadata.step,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': { border: 'none' },
          },
        }}
      />
    </Box>
  );
}

function TextCell({
  metadata,
  sx,
  inputRef,
  tempValue,
  onValueChange,
  onKeyDown,
}: {
  metadata: CellMetadata & { type: 'text' };
  sx: SxProps<Theme>;
  inputRef: RefObject<HTMLInputElement | null>;
  tempValue: IProps['tempValue'];
  onValueChange: IProps['onValueChange'];
  onKeyDown: IProps['onKeyDown'];
}) {
  return (
    <Box sx={{ ...sx, p: 0.5 }}>
      <TextField
        inputRef={inputRef}
        type="text"
        value={tempValue?.toString() ?? ''}
        onChange={(e) => onValueChange?.(e.target.value)}
        onKeyDown={onKeyDown as any}
        size="small"
        fullWidth
        autoFocus
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': { border: 'none' },
          },
        }}
      />
    </Box>
  );
}

const ReadonlyCell = React.forwardRef<
  HTMLDivElement,
  {
    sx: SxProps<Theme>;
    cell: IProps['cell'];
    onClick: IProps['onClick'];
    onMouseDown: IProps['onMouseDown'];
    onMouseEnter: IProps['onMouseEnter'];
    onDoubleClick: IProps['onDoubleClick'];
    onKeyDown: IProps['onKeyDown'];
    disabled: IProps['disabled'];
  }
>(function ReadonlyCell(
  { sx, cell, onClick, onMouseDown, onMouseEnter, onDoubleClick, onKeyDown, disabled },
  ref
) {
  return (
    <Box
      ref={ref}
      component="div"
      role="gridcell"
      sx={sx}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onMouseDown={disabled ? undefined : onMouseDown}
      onMouseEnter={disabled ? undefined : onMouseEnter}
      onDoubleClick={disabled ? undefined : onDoubleClick}
      onKeyDown={disabled ? undefined : onKeyDown}
    >
      {cell?.value?.toString() ?? ''}
    </Box>
  );
});
